// The "No more moves" notice of the card games (owner 2026-10-26, after a player report: a lost Forty Thieves game
// looked "blocked"; an option, off by default: on from 2026-10-30, off again 2026-10-31 after a player's complaint, "to
// pasjans, czasem się układa karty dla samej zabawy układania"). After every move the position goes to a Web Worker
// (assist/lost.worker.ts) which searches every line from it (engine/solver.ts canStillWin); when it proves that none
// wins, the "No moves" window opens with Undo, Retry and New game (windows/SimpleWindows.tsx NoMovesWindow, `cards`).
// It opens once: the moves after it are lost too and stay quiet, and so does coming back to a position it already told
// about (an undo and the same move again: on a slow device the position between them runs out of time, `unknown`); a
// position not proved lost lets it open again for a NEW lost position. Klondike, Double Klondike, Spider, Forty Thieves, FreeCell and Pyramid (2026-10-30, owner: "czy we
// wszystkich działa"); not Mahjong (it has its own, ui/mahjongHelp.ts). In a multiplayer race it is always on and
// ends the player's part by itself, no window (2026-10-29: 4 in 10 games ended with a stuck player leaving,
// .ai/multiplayer-plan.md step 0b; 2026-10-30: the player's score is final at once, the server ends the race as soon
// as the winner is certain).
import type { GameController } from '../engine/controller';
import { RULES } from '../engine/rules';
import type { GameId, GameState } from '../engine/types';
import { canStillWin, type SolveResult } from '../engine/solver';
import { uiStore } from '../store/uiStore';

/** The check starts once the cards have landed and the player has paused for a moment. */
const SETTLE_MS = 700;

/** Every solitaire but Mahjong (its tiles have their own notice, ui/mahjongHelp.ts); not the card games at a table. */
const checked = (game: GameId): boolean => {
  const r = RULES[game];
  return !r.tiles && !r.table;
};

/** Where no worker starts (blocked, or a browser without one): the check runs on the page, this long at most (a lost
 *  end position is proved in a few ms; Chrome 86, Safari 14 and Firefox 78 run the worker, checked 2026-10-26). */
const PAGE_BUDGET_MS = 150;

/** How long one check may run: shorter on a phone or tablet (a coarse pointer). */
const budgetMs = (): number => {
  try {
    return matchMedia('(pointer: coarse)').matches ? 600 : 1200;
  } catch {
    return 1200;
  }
};

/** `stuckOnline`: in a multiplayer race a lost game ends the player's part at once, no question (owner 2026-10-30: "a
 *  player who did all they could should not wait, or they leave"): the score is final and the server ends the race
 *  as soon as the winner is certain. */
export function watchLostGame(ctrl: GameController, multiplayer: () => boolean, stuckOnline: () => void): void {
  let worker: Worker | null = null;
  let timer = 0;
  let asked = 0;
  let seenPiles: unknown = null;
  let deal = -1;
  /** The window may open (reset by a new deal and by any position not proved lost). */
  let armed = true;
  /** The lost positions of this deal the player was already told about. */
  const told = new Set<string>();

  /** One check at a time: a position that comes while one runs waits, and only the newest is checked next. */
  let inFlight = false;
  let workerBroken = false;
  let waiting = false;
  const send = () => {
    const s = ctrl.getState();
    if (!(uiStore().getState().options.option.noMovesNotice || multiplayer()) || !checked(s.game)) return;
    if (s.status !== 'idle' || s.won || s.deals === 0) return;
    if (inFlight) {
      waiting = true;
      return;
    }
    const id = asked;
    const piles = s.piles;
    const answer = (result: SolveResult) => {
      inFlight = false;
      if (waiting) {
        waiting = false;
        send();
      }
      if (id !== asked || ctrl.getState().piles !== piles) return; // the game went on meanwhile
      if (result !== 'stuck') {
        armed = true;
        return;
      }
      const mp = multiplayer();
      if (!armed || !(uiStore().getState().options.option.noMovesNotice || mp)) return;
      const ui = uiStore();
      // not over another window (Options, Menu, ...): the next move asks again
      if (ui.getState().windows.some((w) => !w.closing && !w.minimized)) return;
      armed = false;
      const position = JSON.stringify(piles);
      if (told.has(position)) return;
      told.add(position);
      if (mp) stuckOnline();
      else ui.openWindow('no_moves', { cards: true });
    };
    const state: GameState = { ...s, history: [] };
    inFlight = true;
    if (!workerBroken) {
      try {
        worker ??= new Worker(new URL('../assist/lost.worker.ts', import.meta.url), { type: 'module' });
        worker.onmessage = (e: MessageEvent<{ id: number; result: SolveResult }>) => answer(e.data.result);
        worker.onerror = () => {
          workerBroken = true;
          worker = null;
          answer(canStillWin(state, { maxNodes: 60_000, budgetMs: PAGE_BUDGET_MS, now: () => performance.now() }));
        };
        worker.postMessage({ id, state, budgetMs: budgetMs() });
        return;
      } catch {
        workerBroken = true;
      }
    }
    answer(canStillWin(state, { maxNodes: 60_000, budgetMs: PAGE_BUDGET_MS, now: () => performance.now() }));
  };

  const schedule = () => {
    window.clearTimeout(timer);
    asked += 1; // an answer about an older position is dropped
    timer = window.setTimeout(send, SETTLE_MS);
  };
  let seenStatus = '';
  ctrl.subscribe((s) => {
    if (s.deals !== deal) {
      deal = s.deals;
      armed = true;
      told.clear();
    }
    // a new position, or the end of its animation (the clock's ticks change neither)
    const changed = s.piles !== seenPiles || (s.status === 'idle' && seenStatus !== 'idle');
    seenPiles = s.piles;
    seenStatus = s.status;
    if (changed) schedule();
  });
  // turned on in Options: the position in play is checked once the window is closed
  let on = uiStore().getState().options.option.noMovesNotice;
  let windowsOpen = false;
  uiStore().subscribe(() => {
    const ui = uiStore().getState();
    const now = ui.options.option.noMovesNotice;
    const open = ui.windows.some((w) => !w.closing && !w.minimized);
    if ((now && !on) || (now && windowsOpen && !open)) schedule();
    on = now;
    windowsOpen = open;
  });
}
